import { useEffect, useRef, useState, useCallback } from "react";

const WS_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws") + "/api/ws";

// Authenticated chat socket with auto-reconnect (exponential backoff) and heartbeat.
// The session cookie authenticates the upgrade (same-origin). Consumers get a live
// `status` ("open" | "connecting" | "closed") so the UI can fall back to polling.
export function useChatSocket(onEvent) {
  const [status, setStatus] = useState("connecting");
  const wsRef = useRef(null);
  const handlerRef = useRef(onEvent);
  const retryRef = useRef(0);
  const timerRef = useRef(null);
  const beatRef = useRef(null);
  const aliveRef = useRef(true);
  handlerRef.current = onEvent;

  const clearTimers = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (beatRef.current) clearInterval(beatRef.current);
  };

  const connect = useCallback(() => {
    if (!WS_URL.startsWith("ws")) { setStatus("closed"); return; }
    let ws;
    try { ws = new WebSocket(WS_URL); } catch { scheduleReconnect(); return; }
    wsRef.current = ws;
    setStatus((s) => (s === "open" ? s : "connecting"));
    ws.onopen = () => {
      retryRef.current = 0;
      setStatus("open");
      beatRef.current = setInterval(() => { try { ws.send(JSON.stringify({ t: "ping" })); } catch {} }, 25000);
    };
    ws.onmessage = (e) => { try { handlerRef.current && handlerRef.current(JSON.parse(e.data)); } catch {} };
    ws.onclose = () => { if (beatRef.current) clearInterval(beatRef.current); setStatus("closed"); if (aliveRef.current) scheduleReconnect(); };
    ws.onerror = () => { try { ws.close(); } catch {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scheduleReconnect = useCallback(() => {
    retryRef.current = Math.min(retryRef.current + 1, 6);
    const delay = Math.min(1000 * 2 ** retryRef.current, 15000);
    timerRef.current = setTimeout(() => { if (aliveRef.current) connect(); }, delay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connect]);

  useEffect(() => {
    aliveRef.current = true;
    connect();
    const onVis = () => {
      if (document.visibilityState === "visible" && wsRef.current && wsRef.current.readyState > 1) {
        retryRef.current = 0; connect();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      aliveRef.current = false;
      document.removeEventListener("visibilitychange", onVis);
      clearTimers();
      try { wsRef.current && wsRef.current.close(); } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = useCallback((obj) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) { try { ws.send(JSON.stringify(obj)); return true; } catch {} }
    return false;
  }, []);

  return { status, send };
}
